import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Send, Lock, Crown, Flame, Users as UsersIcon, User as UserIcon, Clock } from "lucide-react";
import VipBadge from "@/components/app/VipBadge";

const POLL_MS = 4000;

export default function Events() {
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [recent, setRecent] = useState([]);
  const [thread, setThread] = useState("public");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const lastTsRef = useRef(null);

  const load = async () => {
    const [t, r] = await Promise.all([
      api.get("/events/today"),
      api.get("/events/recent", { params: { days: 7 } }),
    ]);
    setEvent(t.data);
    setRecent(r.data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!event) return;
    if (thread === "vip" && !event.vip_thread_unlocked) return;
    lastTsRef.current = null;
    setMessages([]);
    api.get("/chat/messages", { params: { event_id: event.event_id, thread, limit: 100 } })
      .then((r) => {
        setMessages(r.data);
        if (r.data.length) lastTsRef.current = r.data[r.data.length - 1].created_at;
        requestAnimationFrame(bottomize);
      });
  }, [event, thread]);

  useEffect(() => {
    if (!event) return;
    const int = setInterval(async () => {
      try {
        if (!lastTsRef.current) return;
        if (thread === "vip" && !event.vip_thread_unlocked) return;
        const { data } = await api.get(`/chat/messages/since/${encodeURIComponent(lastTsRef.current)}`, {
          params: { event_id: event.event_id, thread },
        });
        if (data.length) {
          setMessages((prev) => [...prev, ...data]);
          lastTsRef.current = data[data.length - 1].created_at;
          requestAnimationFrame(bottomize);
        }
      } catch {}
    }, POLL_MS);
    return () => clearInterval(int);
  }, [event, thread]);

  const bottomize = () => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  };

  const send = async (e) => {
    e?.preventDefault();
    const content = input.trim();
    if (!content || sending || !event) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", {
        content,
        event_id: event.event_id,
        thread,
      });
      setMessages((prev) => [...prev, data]);
      lastTsRef.current = data.created_at;
      setInput("");
      requestAnimationFrame(bottomize);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!event) return <div className="p-10 text-center font-mono text-xs text-[var(--text-muted)]">LOADING…</div>;

  const hoursLeft = Math.floor(event.seconds_until_next / 3600);
  const minsLeft = Math.floor((event.seconds_until_next % 3600) / 60);

  const vipLocked = thread === "vip" && !event.vip_thread_unlocked;
  const currentPrompt = thread === "vip" ? event.vip_prompt : event.prompt;

  return (
    <div className="max-w-[1300px] mx-auto px-4 md:px-6 py-6 md:py-10 relative z-10">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="tape-label">DAILY MOG-OFF</span>
          <h1 className="font-display text-5xl md:text-7xl mt-3 leading-none">
            <span className="text-[var(--primary)] glow">TODAY'S</span> ARENA
          </h1>
          <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mt-2">
            {event.category} · Resets in <Clock className="inline w-3 h-3 mx-1" /> {hoursLeft}h {minsLeft}m
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">participation</div>
          <div className="flex items-center gap-3 mt-1 justify-end">
            <div className="text-sm font-mono">
              <UsersIcon className="inline w-3.5 h-3.5 text-[var(--primary)] mr-1" />
              {event.public_thread_count} public
            </div>
            <div className="text-sm font-mono">
              <Crown className="inline w-3.5 h-3.5 text-[var(--primary)] mr-1" />
              {event.vip_thread_count} VIP
            </div>
          </div>
        </div>
      </div>

      {/* Prompt card */}
      <div className="panel-bright p-6 mb-6" data-testid="event-prompt">
        <div className="flex items-center gap-2 mb-3">
          <Flame className="w-5 h-5 text-[var(--primary)] glow" />
          <span className="tape-label">{thread === "vip" ? "VIP PROMPT" : "TODAY'S PROMPT"}</span>
        </div>
        <p className="font-display text-2xl md:text-3xl leading-snug">
          {currentPrompt}
        </p>
      </div>

      {/* Thread switcher */}
      <div className="grid grid-cols-2 gap-px bg-[var(--border)] mb-4">
        <button
          onClick={() => setThread("public")}
          data-testid="thread-public"
          className={`p-4 text-left transition-all ${thread === "public" ? "bg-[var(--surface-2)] border-l-4 border-[var(--primary)]" : "bg-[var(--bg)] hover:bg-[var(--surface)]"}`}
        >
          <div className="flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-[var(--primary)]" />
            <span className="font-display text-xl">PUBLIC THREAD</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
            Everyone can post
          </div>
        </button>
        <button
          onClick={() => setThread("vip")}
          data-testid="thread-vip"
          className={`p-4 text-left transition-all relative ${thread === "vip" ? "bg-[var(--surface-2)] border-l-4 border-[var(--primary)]" : "bg-[var(--bg)] hover:bg-[var(--surface)]"}`}
        >
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-[var(--primary)] glow" />
            <span className="font-display text-xl">VIP THREAD</span>
            {!event.vip_thread_unlocked && (
              <span className="tape-label flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> VIP</span>
            )}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
            Ascended-only. Deeper prompts + no lurkers.
          </div>
        </button>
      </div>

      {/* Feed + composer */}
      <div className="panel flex flex-col h-[60vh] mb-6">
        <div ref={listRef} className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4" data-testid="event-messages">
          {vipLocked ? (
            <VipLocked />
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Flame className="w-8 h-8 text-[var(--primary)] mb-2" />
              <div className="font-display text-2xl">BE FIRST.</div>
              <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
                Break the seal on today's mog-off.
              </div>
            </div>
          ) : (
            messages.map((m) => <Row key={m.id} m={m} isMe={m.user_id === user?.id} />)
          )}
        </div>
        {!vipLocked && (
          <form onSubmit={send} className="border-t border-[var(--border)] p-3 flex gap-2 bg-[var(--surface)]">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={500}
              placeholder={`Post to the ${thread === "vip" ? "VIP" : "public"} thread…`}
              className="brutal"
              data-testid="event-input"
            />
            <button type="submit" disabled={!input.trim() || sending} className="btn-brutal px-4" data-testid="event-send">
              <Send className="w-4 h-4" />
            </button>
          </form>
        )}
      </div>

      {/* Recent events */}
      <div>
        <span className="tape-label">RECENT DAYS</span>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)] mt-3">
          {recent.map((ev) => (
            <div key={ev.event_id} className="bg-[var(--surface)] p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--primary)]">{ev.date}</div>
              <div className="text-xs font-mono text-white mt-1">{ev.category}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">{ev.post_count} posts</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ m, isMe }) {
  return (
    <div className="flex gap-3" data-testid={`event-msg-${m.id}`}>
      <div className="w-8 h-8 border border-[var(--border-bright)] bg-[var(--surface-2)] flex items-center justify-center overflow-hidden shrink-0">
        {m.avatar_base64 ? (
          <img src={`data:image/jpeg;base64,${m.avatar_base64}`} alt="" className="w-full h-full object-cover" />
        ) : (
          <UserIcon className="w-4 h-4 text-[var(--text-muted)]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs font-mono flex-wrap">
          <span className={isMe ? "text-[var(--primary)] font-bold" : "text-white"}>@{m.username}</span>
          <VipBadge user={m} size="sm" />
          <span className="text-[10px] text-[var(--text-muted)]">
            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
        <div className="text-sm text-[var(--text)] mt-0.5 break-words whitespace-pre-wrap">{m.content}</div>
      </div>
    </div>
  );
}

function VipLocked() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-10">
      <Lock className="w-10 h-10 text-[var(--primary)] mb-4" />
      <div className="font-display text-4xl mb-2">
        VIP <span className="text-[var(--primary)] glow">THREAD LOCKED</span>
      </div>
      <p className="text-sm text-[var(--text-muted)] mb-6 max-w-md">
        The VIP thread runs a deeper prompt, no lurkers, and cross-references your scans. Unlock with any VIP plan.
      </p>
      <Link to="/vip" className="btn-brutal" data-testid="event-unlock-cta">
        <Crown className="inline w-4 h-4 mr-1" /> Become ASCENDED →
      </Link>
    </div>
  );
}
